import {
  AuthHandlersApi,
  UserHandlersApi,
  CompanyHandlersApi,
  HoaHandlersApi,
  HomeHandlersApi,
  MemberHandlersApi,
  ViolationHandlersApi,
  MarketingApi,
  CommentHandlersApi,
  FileHandlersApi,
  MemberAuthApi,
  NotificationHandlersApi,
  WorkOrderHandlersApi,
  VendorHandlersApi,
  BankApi,
  BillingHandlersApi,
  JobsApi,
  LedgerHandlersApi,
  AmenityExtrasApi,
  AmenityGroupHandlersApi,
  AmenityHandlersApi,
  ReservationHandlersApi,
  ReservationOperationsApi,
  Configuration,
} from './rust-api-client';
import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import {
  clearAllTokens,
  getRefreshToken,
  getRememberMe,
  getToken,
  refreshAccessToken,
  setToken,
  tokenIsUnexpired,
} from '../utils/TokenUtils';

// Single base URL for the unified Rust backend
const API_BASE_URL = window._env_.REACT_APP_AUTH_SVC;

// Initialize the API configuration
const apiConfig = new Configuration({
  basePath: API_BASE_URL,
});

// Create axios instance for authenticated requests
const apiAxiosInstance: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
});

// Create separate instance for unauthenticated requests (auth endpoints)
const authAxiosInstance: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
});

/** Remember where to come back to after signing in again. */
export const saveCurrentLocation = () => {
  // Only save if we're not already on the login page
  if (!window.location.pathname.includes('/login')) {
    const location = {
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
    };
    sessionStorage.setItem('returnTo', JSON.stringify(location));
  }
};

// Common request interceptor
const requestInterceptor = async (
  config: InternalAxiosRequestConfig
): Promise<InternalAxiosRequestConfig> => {
  let token = getToken();

  if (token) {
    const isUnexpired = await tokenIsUnexpired(token);

    if (!isUnexpired) {
      const refreshToken = getRefreshToken();
      if (refreshToken) {
        const newToken = await refreshAccessToken(refreshToken);
        if (newToken) {
          token = newToken;
          setToken(newToken, !!getRememberMe());
        } else {
          // Refresh failed, clear tokens and redirect to login
          clearAllTokens();
          saveCurrentLocation();
          window.location.href = '/login';
          throw new axios.Cancel(
            'Operation canceled due to token refresh failure.'
          );
        }
      } else {
        // No refresh token available, clear tokens and redirect to login
        clearAllTokens();
        saveCurrentLocation();
        window.location.href = '/login';
        throw new axios.Cancel(
          'Operation canceled due to missing refresh token.'
        );
      }
    }

    config.headers = config.headers || {};
    config.headers['Authorization'] = `Bearer ${token}`;
  }

  return config;
};

/**
 * The endpoints whose 401 says nothing about the session.
 *
 * `change-password` answers 401 when the **current password** field is
 * wrong. The token that carried the request is perfectly valid — the
 * failed credential check is the one in the form, not the one in the
 * header. Treating it like any other 401 would sign somebody out and
 * discard the form they were filling in because they mistyped a
 * password, which is the opposite of what the screen is for.
 *
 * Matched on the path suffix so it covers both realms:
 * `/api/v1/auth/change-password` and
 * `/api/v1/member-portal/auth/change-password`.
 */
const SESSION_INDEPENDENT_401 = /\/auth\/change-password$/;

// Common response interceptor to redirect 401 to login page
const responseInterceptor = (error: any) => {
  const requestUrl: string = error.config?.url ?? '';
  if (
    error.response &&
    error.response.status === 401 &&
    !SESSION_INDEPENDENT_401.test(requestUrl)
  ) {
    saveCurrentLocation();
    clearAllTokens();
    window.location.href = '/login';
  }
  return Promise.reject(error);
};

// Apply interceptors to authenticated axios instance
apiAxiosInstance.interceptors.request.use(requestInterceptor, (error) =>
  Promise.reject(error)
);
apiAxiosInstance.interceptors.response.use(
  (response) => response,
  responseInterceptor
);

// Auth service (uses unauthenticated instance for login/register)
export const authService = new AuthHandlersApi(
  apiConfig,
  API_BASE_URL,
  authAxiosInstance
);

/**
 * The one auth endpoint that has to be called *as somebody*.
 *
 * `authService` above is built on the unauthenticated instance on purpose:
 * login, password reset and refresh must not carry a stale token or set the
 * refresh interceptor going. `logout` is the exception — the server ends the
 * session belonging to the bearer token, so without the Authorization header
 * it answers 401 and revokes nothing. Sign-out then looked successful while
 * the token stayed valid, which is the opposite of the point.
 *
 * Using the authenticated instance also means an expired access token is
 * refreshed on the way out, so a long-idle tab can still end its session
 * rather than leaving the refresh token alive for its remaining days.
 */
export const authSessionService = new AuthHandlersApi(
  apiConfig,
  API_BASE_URL,
  apiAxiosInstance
);

// User service
export const userService = new UserHandlersApi(
  apiConfig,
  API_BASE_URL,
  apiAxiosInstance
);

// Company service
export const companyService = new CompanyHandlersApi(
  apiConfig,
  API_BASE_URL,
  apiAxiosInstance
);

// HOA service
export const hoaService = new HoaHandlersApi(
  apiConfig,
  API_BASE_URL,
  apiAxiosInstance
);

// Home service
export const homeService = new HomeHandlersApi(
  apiConfig,
  API_BASE_URL,
  apiAxiosInstance
);

// Member service
export const memberService = new MemberHandlersApi(
  apiConfig,
  API_BASE_URL,
  apiAxiosInstance
);

// Violation service
export const violationService = new ViolationHandlersApi(
  apiConfig,
  API_BASE_URL,
  apiAxiosInstance
);

// Marketing service (public endpoints)
export const marketingService = new MarketingApi(
  apiConfig,
  API_BASE_URL,
  authAxiosInstance
);

// Comment service (new in Rust backend)
export const commentService = new CommentHandlersApi(
  apiConfig,
  API_BASE_URL,
  apiAxiosInstance
);

// File service (new in Rust backend)
export const fileService = new FileHandlersApi(
  apiConfig,
  API_BASE_URL,
  apiAxiosInstance
);

// Member Auth service (new in Rust backend - for member portal)
export const memberAuthService = new MemberAuthApi(
  apiConfig,
  API_BASE_URL,
  authAxiosInstance
);

// Notification service
export const notificationService = new NotificationHandlersApi(
  apiConfig,
  API_BASE_URL,
  apiAxiosInstance
);

// Work Order service
export const workOrderService = new WorkOrderHandlersApi(
  apiConfig,
  API_BASE_URL,
  apiAxiosInstance
);

// Vendor service
export const vendorService = new VendorHandlersApi(
  apiConfig,
  API_BASE_URL,
  apiAxiosInstance
);

// Bank service (bank connections, accounts, transactions, statements)
export const bankService = new BankApi(
  apiConfig,
  API_BASE_URL,
  apiAxiosInstance
);

// Billing service (the homeowner ledger: dues, payments, collections,
// statements, estoppel). Money endpoints, so read
// `docs/DUES_FRONTEND_PLAN.md` before using them — amounts are decimal
// strings and must never be parsed into numbers.
export const billingService = new BillingHandlersApi(
  apiConfig,
  API_BASE_URL,
  apiAxiosInstance
);

// Scheduled jobs (run history, per-HOA outcomes, manual triggers).
// Platform-wide reads and the trigger are SuperAdmin-only; the per-HOA
// history is scoped to that association's company.
export const jobsService = new JobsApi(
  apiConfig,
  API_BASE_URL,
  apiAxiosInstance
);

// General ledger (the association's own books: funds, the chart of
// accounts, periods, reports, bank reconciliation, budgets). A different
// axis from `billingService` — that one is a homeowner's account, this
// one is `hoa_id` x `fund_id` x `account_id`, and the two-ledgers
// decision in `back-end-rs/docs/GL_DESIGN.md` is why they do not merge.
//
// Amounts here are decimal strings for the same reason as billing: the
// journal produced them and parsing one into a number is how a report
// stops agreeing with the books.
export const ledgerService = new LedgerHandlersApi(
  apiConfig,
  API_BASE_URL,
  apiAxiosInstance
);

// Amenities (#640): what an association offers, its hours, booking
// rules, fee, agreement versions, closures and photos. Updates are a full
// replacement — start from `amenityToUpdate` in reservation/reservationMeta.
export const amenityService = new AmenityHandlersApi(
  apiConfig,
  API_BASE_URL,
  apiAxiosInstance
);

// Reservations (#640): the approval queue, a booking's decision and
// history, booking on a household's behalf, and the HOA's reservation
// policy. Times are the amenity's wall-clock (`*_local`); a fee is a
// decimal string posted to the home's ledger on approval.
export const reservationService = new ReservationHandlersApi(
  apiConfig,
  API_BASE_URL,
  apiAxiosInstance
);

// Reservations phase 3 (#640): an amenity's pricing rules, required
// documents and waitlist; amenity groups (interchangeable courts and
// rooms); and a booking's operations — deposit settlement, inspections,
// documents, check-in, suspensions and the revenue report.
export const amenityExtrasService = new AmenityExtrasApi(
  apiConfig,
  API_BASE_URL,
  apiAxiosInstance
);
export const amenityGroupService = new AmenityGroupHandlersApi(
  apiConfig,
  API_BASE_URL,
  apiAxiosInstance
);
export const reservationOperationsService = new ReservationOperationsApi(
  apiConfig,
  API_BASE_URL,
  apiAxiosInstance
);

// Export axios instances for use in components that need direct access
export { authAxiosInstance, apiAxiosInstance };

// Legacy aliases for backwards compatibility during migration
export const hoaAxiosInstance = apiAxiosInstance;
export const violationAxiosInstance = apiAxiosInstance;
export const companyAxiosInstance = apiAxiosInstance;
export const memberAxiosInstance = apiAxiosInstance;
